import { useEffect, useState } from "react";
import { Box } from "ui/box";
import { Stack } from "ui/stack";

import { DesignPreview } from "containers/admin/clients/touchpoint/components/design-preview/design-preview";
import EmptyDesignPreview from "containers/admin/clients/touchpoint/components/empty-design-preview/empty-design-preview";
import { BuilderHtmlPreview } from "containers/admin/clients/touchpoint/components/builder/builder-html-preview/builder-html-preview";
import {
  builderPreviewMenuOptions,
  BuilderPreviewPaneHeader,
  BuilderPreviewViews,
} from "containers/admin/clients/touchpoint/components/builder/builder-preview-pane-header/builder-preview-pane-header";

import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useBuilderContent, useSetTouchpointVersionCache } from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";
import type { LandingPageAttributes } from "hooks/api/use-touchpoint-versions";

const touchpointVersionApi = getTouchpointVersion();

import { removeUserIdFromString } from "utilities/touchpoint";

import { useLocation } from "react-router";
import { Route as AppRoutes } from "utilities/app-routes";

const LPBuilderPreviewTab = ({ previewMode: isPreviewMode }: { previewMode: boolean }) => {
  const currentVersion = useBuilderContent() as LandingPageAttributes;
  const currentTouchpoint = useCurrentTouchpoint();
  const location = useLocation();
  const setTouchpointVersionCache = useSetTouchpointVersionCache({
    clientId: currentTouchpoint.clientId,
    campaignId: currentTouchpoint.campaignId,
    touchpointId: currentTouchpoint.id,
  });
  const [isCommentMode, setIsCommentMode] = useState(false);
  const [isFullContentMode, setIsFullContentMode] = useState(false);

  const { desktopView, mobileView } = builderPreviewMenuOptions;
  const [currentView, setCurrentView] = useState<BuilderPreviewViews>(mobileView);
  const isMobilePreview = currentView === mobileView;
  const isDesktopPreview = currentView === desktopView;
  const isDesignPreview = isDesktopPreview || isMobilePreview;

  useEffect(() => {
    if (isPreviewMode) {
      touchpointVersionApi
        .getOneVersion({
          clientId: currentTouchpoint.clientId,
          campaignId: currentTouchpoint.campaignId,
          touchpointId: currentTouchpoint.id,
          id: currentVersion.id,
        })
        .then(mapTouchpointVersionDTO)
        .then(setTouchpointVersionCache);
    }
  }, [currentVersion, currentTouchpoint, isPreviewMode, setTouchpointVersionCache]);

  const builderTestId = isDesktopPreview
    ? "lp-builder-desktop-previewType"
    : "lp-builder-mobile-previewType";

  const maxWidthClass = isMobilePreview ? "max-w-[25rem]" : `max-w-calc(100% - 0.5rem)`;

  const designTabRedirect = location.pathname.replace(AppRoutes.preview, AppRoutes.design);
  const previewHtml = currentVersion.finalHtml
    ? removeUserIdFromString(currentVersion.finalHtml)
    : "";

  return (
    <Stack
      data-testid={builderTestId}
      spacing={0}
      sx={{ alignItems: "center", flex: "1", mx: "auto", maxWidth: "1024px", width: "100%" }}
    >
      <BuilderPreviewPaneHeader
        currentView={currentView}
        setCurrentView={setCurrentView}
        isCommentMode={isCommentMode}
        setIsCommentMode={setIsCommentMode}
        isTouchpointPreview={false}
        isFullContentMode={isFullContentMode}
        setIsFullContentMode={setIsFullContentMode}
      />
      <Box className={`w-full ${maxWidthClass}`}>
        {isDesignPreview ? (
          currentVersion.finalHtml && currentVersion.data?.jsonOutput ? (
            <DesignPreview
              htmlContent={previewHtml}
              htmlJSON={currentVersion.data?.jsonOutput}
              isCommentMode={isCommentMode}
              isFullContentMode={isFullContentMode}
              mobileView={isMobilePreview}
            />
          ) : (
            <EmptyDesignPreview redirectTo={designTabRedirect} currentVersion={currentVersion} />
          )
        ) : (
          <BuilderHtmlPreview
            data-testid="lp-builder-html"
            textareaTestId="lp-builder-advanced-textarea"
            value={currentVersion.finalHtml}
          />
        )}
      </Box>
    </Stack>
  );
};

export default LPBuilderPreviewTab;
